/**
 * Utilities for talking to the Atlassian outbound-auth gateway, which is the
 * canonical source of truth for whether a user has a working OAuth connection
 * to a given Smart Link connector.
 *
 * Why this exists
 * ---------------
 * Previously we relied on the CDS endpoint
 *   GET /gateway/api/third-party-configuration/connected-data-sources/{cloudId}
 * (via `get3pSearchConfig`) to determine both the OAuth start URL and whether
 * the user was already connected. That endpoint has four well-known problems
 * for the Confluence onboarding "Connect your 3P tools" experience:
 *
 *   1. Edition-gated: CDS only works on Standard+ tenants with Rovo
 *      provisioned. Free tier tenants return 403.
 *   2. Lazy-init: the registry behind CDS (ERS) does not have a
 *      `smartlinks-connector-{provider}` row until a Smart Link resolve event
 *      for that provider has fired on the tenant. Brand-new tenants are
 *      effectively empty.
 *   3. Not a definitive provider universe: CDS only enumerates what's already
 *      been touched on this tenant, not what's available to connect.
 *   4. No back-population: even after a user connects via a fallback path,
 *      CDS does not see it (until/unless ERS gets the event), so downstream
 *      surfaces like the post-office banner keep re-prompting.
 *
 * The outbound-auth gateway endpoint
 *   GET /gateway/api/outbound-auth/aaId/{aaId}
 * powers id.atlassian.com/manage-profile/apps. It is cookie-authed (no ASAP
 * needed), works cross-origin from *.atlassian.net, and returns the user's
 * full OAuth-grant list with a per-account `status` field. This is the same
 * data the Atlassian ID Manage Profile UI uses.
 *
 * Canonical container UUIDs come from
 *   ai-3p-connector/connector-service/implementation/src/main/resources/application-prod.yml
 *   ai-3p-connector/connector-service/implementation/src/main/resources/application.yml      (staging)
 * under `linking-platform.providers[]`. The `containerId` + `serviceKey`
 * tuples in the outbound-auth response match those values exactly.
 *
 * Backwards-compat / defensive design
 * -----------------------------------
 *  - All network/parse failures return `null` and let callers fall back to
 *    whatever they were doing before. This module never throws.
 *  - The container maps are `Partial<Record<...>>` and helpers return `null`
 *    when a product has no mapping. Callers are expected to filter such
 *    providers out rather than render broken Connect buttons.
 */

import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { fetchAccountsByAaId } from '@atlassian/search-client/fetchAccountsByAaId';
import type {
	Container as OutboundAuthContainer,
	GetAccountsByAaIDResponse,
} from '@atlassian/search-client/outbound-auth/types';

// ---------------------------------------------------------------------------
// Types — re-export the canonical outbound-auth payload shape from
// `@atlassian/search-client` so call sites don't need a second dependency.
// ---------------------------------------------------------------------------

/**
 * The outbound-auth gateway's response shape. Re-exported from
 * `@atlassian/search-client` (which is the canonical AFM client for this
 * endpoint and provides built-in caching + degradation handling).
 *
 * Shape:
 *   { containers: [{ container: { containerId }, services: [{ service: { serviceKey }, accounts: [{ status }] }] }] }
 *
 * `status` is one of `available | expired | refreshNeeded | serverError`.
 * Only `available` is considered "currently connected" for our purposes —
 * `expired` and `refreshNeeded` mean the user has connected in the past but
 * the token is no longer usable without re-consent, so we still show the
 * Connect CTA.
 */
export type OutboundAuthAccountsPayload = GetAccountsByAaIDResponse;

// ---------------------------------------------------------------------------
// Canonical container UUIDs.
//
// Source of truth:
//   ai-3p-connector/connector-service/implementation/src/main/resources/application-prod.yml
//   ai-3p-connector/connector-service/implementation/src/main/resources/application.yml
// under `linking-platform.providers[]`.
//
// Confluence page with the same mapping (human-readable):
//   https://hello.atlassian.net/wiki/spaces/~712020a0f8b99f05e4498da4c6372974b91a44/pages/6649924469
//
// NOTE: This map is intentionally a `Partial<Record<...>>`. Providers without
// a mapping are filtered out by callers rather than rendered with a broken
// Connect URL. Add new entries as they ship in ai-3p-connector — entries below
// marked with `// VERIFIED` were confirmed live against the outbound-auth
// gateway response. Other entries are TODO: pull from the YAML before
// expanding rollout to those providers.
//
// Follow-up Jira: replace these hardcoded UUIDs with a registry call once
// ai3p-user-api's "structured OAuth container model" ships.
// ---------------------------------------------------------------------------

interface SmartLinkContainerMapping {
	containerId: string;
	// Smart Link narrow scopes. Set only when the container's gateway default
	// is broader than what Smart Links needs (e.g. gdrive's kitchen-sink set).
	defaultScopes?: string[];
	serviceKey: string;
}

// Verified identical in prod and staging gdrive CDS URLs.
const SMARTLINK_GDRIVE_SCOPES = [
	'https://www.googleapis.com/auth/drive.readonly',
	'https://www.googleapis.com/auth/userinfo.profile',
	'https://www.googleapis.com/auth/userinfo.email',
];

// Canonical container UUIDs are mirrored from the "Smartlinks Connector -
// Container IDs (Staging & Production)" Confluence page (page id 6649924469),
// which is itself sourced from ai-3p-connector application.yml /
// application-prod.yml. Each prod entry below was cross-checked against the
// live `outboundAuthUrl` values in
// `platform/packages/linking-platform/rovo-link-picker/mocks/mockConfigInfo.json`
// (which holds the full canonical set used by rovo-link-picker), to confirm
// the prod UUIDs and the exact `serviceKey` strings the OAuth gateway
// expects.
//
// Coverage notes:
//   • Every Smart Link provider that has a UNIQUE container in the
//     ai-3p-connector YAML is mapped here. We deliberately do NOT map
//     "shared container only" providers (Adobe Sign, Aha, Databricks,
//     Monday, ServiceNow, Zendesk) because their container is the shared
//     SmartLinks container — knowing a user has an `available` account in
//     that shared container is not sufficient to claim they are connected
//     to any specific one of those providers.
//   • Outlook Mail / Confluence DC / Jira DC / Gmail / Google Calendar are
//     not in the smartlinks-connector flow at all (no per-provider
//     serviceKey in the canonical mock), so they are excluded.
// Provider count: 33 in prod, 33 in staging (matched pairs).
//
// Follow-up Jira: replace these hardcoded UUIDs with a registry call once
// ai3p-user-api's "structured OAuth container model" ships.

export const SMARTLINK_CONTAINERS_PROD: Partial<
	Record<ProductKeys3PType, SmartLinkContainerMapping>
> = {
	[ProductKeys3P.GoogleDrive]: {
		containerId: 'ff32b4a9-0056-4c40-bb6e-eebf3b1d82f7_c50f3d56-68fc-4c3c-8191-6717f173dc54',
		serviceKey: 'gdrive',
		defaultScopes: SMARTLINK_GDRIVE_SCOPES,
	},
	[ProductKeys3P.Slack]: {
		containerId: '25ab8ceb-e2e9-4b3a-b4ef-902049561d3a_74232f16-3f89-4bc8-9dcb-b5481bca783e',
		serviceKey: 'slack',
	},
	[ProductKeys3P.OneDrive]: {
		containerId: '00f40f12-faac-4ac8-8d6d-310f96ac97f5_f039c3c2-7e00-41c4-b1cf-70743efbf91a',
		serviceKey: 'onedrive',
	},
	[ProductKeys3P.Sharepoint]: {
		containerId: '7289b5af-9aa4-4977-bb8b-6876502a6466_e7e24a0a-0b03-4ef8-9a83-642e6ef97dbc',
		serviceKey: 'sharepoint',
	},
	[ProductKeys3P.Github]: {
		containerId: 'c364f950-69f5-4812-9716-ed71a8d3d408_a57ca83d-597f-4de9-8dca-f9d3cd18d3a7',
		serviceKey: 'github',
	},
	[ProductKeys3P.Figma]: {
		containerId: '7f89dc0c-5c1e-4b71-997f-220bf92361aa_73fbf758-c657-4f06-8edd-f721911f7f53',
		serviceKey: 'figma',
	},
	[ProductKeys3P.Teams]: {
		containerId: 'fe8ece6c-97b6-45ac-959d-865da9a33409_61030824-303b-4751-bca5-1def33b6b0d0',
		serviceKey: 'ms-teams',
	},
	[ProductKeys3P.Dropbox]: {
		containerId: 'd4d6a241-1008-42cf-b2d6-d0b380c096d6_b6796989-c34a-4e19-a4e1-ccc7655f8fd6',
		serviceKey: 'dropbox',
	},
	[ProductKeys3P.Airtable]: {
		containerId: 'c5805f5a-3e9a-48a5-a8f5-7b04aac4ad16_572b2748-e9b1-4161-b36d-19f962646d1a',
		serviceKey: 'airtable',
	},
	[ProductKeys3P.LaunchDarkly]: {
		containerId: '30ba2eba-6d3f-4124-8060-0ea9a9c9e990_d28912a6-171c-41a2-9adb-1426eccbc767',
		serviceKey: 'launchdarkly',
	},
	[ProductKeys3P.ClickUp]: {
		containerId: '12967bc2-803e-4f8d-9e66-1aa48773a5a4_13a589a5-f514-4b63-a104-7d454b8dc676',
		serviceKey: 'clickup',
	},
	[ProductKeys3P.Pipedrive]: {
		containerId: '1edf2d8e-c159-4ffe-8be0-165821a0ecf1_743950e9-2685-4558-a5b0-ae5d3b47853c',
		serviceKey: 'pipedrive',
	},
	[ProductKeys3P.Gitlab]: {
		containerId: '790bd6ce-7ef8-404a-a9dc-798feb10b12b_049b63cb-90b1-4bfa-aa02-f3fdd9298b03',
		serviceKey: 'gitlab',
	},
	[ProductKeys3P.Docusign]: {
		containerId: '35cd5415-c24c-457e-af7c-ac84df8c8628_0a7329e4-b658-41a1-89c2-419514a5ca08',
		serviceKey: 'docusign',
	},
	[ProductKeys3P.Dovetail]: {
		containerId: 'b51f1664-8705-4938-a9a5-02a7eb3395cb_cba4f674-8eea-43aa-874f-8f16af6b75cb',
		serviceKey: 'dovetail',
	},
	[ProductKeys3P.Box]: {
		containerId: '3a79e004-2e3e-45fe-8a21-84f53568a8ee_35421d47-ad3e-44f5-9cde-60bfa869e0b0',
		serviceKey: 'box',
	},
	[ProductKeys3P.AdobeXD]: {
		containerId: '80c22a47-9ca7-4690-88db-65b375f402a7_ad251cf5-828d-494d-b555-4294d9654173',
		serviceKey: 'adobexd',
	},
	[ProductKeys3P.Asana]: {
		containerId: '2487d697-ed1e-4cf5-9655-f1fb1355c341_a927cae4-3500-4386-9ac3-28e87671c2c8',
		serviceKey: 'asana',
	},
	[ProductKeys3P.Smartsheet]: {
		containerId: '101a27da-d409-4f8c-9c75-326eec4fd5d4_fe89f892-8c94-4574-88f7-5898c05be186',
		serviceKey: 'smartsheet',
	},
	[ProductKeys3P.Mural]: {
		containerId: 'cc21faf3-19fc-4522-af92-295866ef82e7_373f2501-6f74-4c0f-9bfa-c54cdea00066',
		serviceKey: 'mural',
	},
	[ProductKeys3P.PowerBI]: {
		containerId: '9c59191f-63ab-44b2-a2cb-2f20adc75365_556e62ba-588b-494d-8c6c-b672e50537f6',
		serviceKey: 'power-bi',
	},
	[ProductKeys3P.Miro]: {
		containerId: 'd01d8332-4637-4ea4-8b14-17a9d1a61504_a894175f-cb17-4e45-88fa-c348967f379c',
		serviceKey: 'miro',
	},
	[ProductKeys3P.Hubspot]: {
		containerId: 'da0f0482-f78f-4eec-b7e6-0bc98ca5a54c_707406e8-d607-427a-b32e-e1d4710c0f2d',
		serviceKey: 'hubspot',
	},
	[ProductKeys3P.Notion]: {
		containerId: '2bc172f6-c553-4fe1-85d4-3e2e087f84f6_be0969ca-ea7e-4685-abaa-7de142c01b36',
		serviceKey: 'notion',
	},
	[ProductKeys3P.Sentry]: {
		containerId: '977cb3af-b6e9-4450-9ce5-bf40f9c73dff_3a8bcf5e-44ab-4f5b-b18d-340027b6a97e',
		serviceKey: 'sentry',
	},
	[ProductKeys3P.PagerDuty]: {
		containerId: '49cdc160-0ad5-4dbf-adad-6a57fb082774_5cef1b1a-44aa-409e-97b3-b90065cbb39e',
		serviceKey: 'pagerduty',
	},
	[ProductKeys3P.Salesforce]: {
		containerId: '2b45cadc-67a2-4902-8148-03e4de5ba747_db858b85-c098-45e7-96c8-9ccc9caf72da',
		serviceKey: 'salesforce',
	},
	[ProductKeys3P.Amplitude]: {
		containerId: '1e30cfdf-cceb-43f4-b34a-dde94e1f9b21_939acc6f-274a-42da-acdc-8af5a14fe698',
		serviceKey: 'amplitude',
	},
	[ProductKeys3P.Lucid]: {
		containerId: '9262005e-5c1a-49af-9ec0-4a354f581c73_80f25935-3f1f-40e1-9b2a-35db54297ebf',
		serviceKey: 'lucid',
	},
	[ProductKeys3P.Zeplin]: {
		containerId: 'a401d4f0-b3d1-4b64-a85d-3952bd42d9a7_3da3c5e7-89db-4330-878f-dff1fe878556',
		serviceKey: 'zeplin',
	},
	[ProductKeys3P.Todoist]: {
		containerId: '0202ace9-65df-40e3-b539-d831b446dad9_c7809c53-31e9-4fc0-8df9-8b8dcb418bb8',
		serviceKey: 'todoist',
	},
	[ProductKeys3P.Stripe]: {
		containerId: 'bb81e8b8-7d0d-4a24-bb5b-acf31ab97bbc_f38b2a60-30a9-4abd-9086-efb78f6986ce',
		serviceKey: 'stripe',
	},
	[ProductKeys3P.AzureDevops]: {
		// Sourced from ai-3p-connector application-prod.yml
		// (permission.linking-platform-info[container=6b34a992…]).
		containerId: '6b34a992-af2a-4a16-8a4d-89038c48a355_719d2d91-a89a-46f5-bacc-e3baf87d21c8',
		serviceKey: 'azuredevops',
	},
	[ProductKeys3P.Webex]: {
		// Sourced from ai-3p-connector application-prod.yml
		// (permission.linking-platform-info[container=18025f11…]).
		containerId: '18025f11-aafe-49ca-ad45-d48f7005e459_19846c70-0c88-44f6-a7c1-d5bdd77c606c',
		serviceKey: 'webex',
	},
};

export const SMARTLINK_CONTAINERS_STAGING: Partial<
	Record<ProductKeys3PType, SmartLinkContainerMapping>
> = {
	[ProductKeys3P.GoogleDrive]: {
		containerId: '2357cbb7-bf5c-4fe4-a9c6-c8e172ccb5b1_3b53863d-a6bb-4818-91da-4de04f975f48',
		serviceKey: 'gdrive',
		defaultScopes: SMARTLINK_GDRIVE_SCOPES,
	},
	[ProductKeys3P.Slack]: {
		containerId: '32a368b4-220e-45a9-b65f-df104dc2e559_7ab21f60-b89a-44c1-bed3-00282c3e7b9e',
		serviceKey: 'slack',
	},
	[ProductKeys3P.OneDrive]: {
		containerId: '6b74c0f8-ce5e-43b0-a68e-eba0fed27cea_4254f605-625b-4026-904a-bdf8ee6de413',
		serviceKey: 'onedrive',
	},
	[ProductKeys3P.Sharepoint]: {
		containerId: '03d0881e-f10f-4751-ab11-27592812060e_b269c883-58b9-463a-9b77-50b6cd8da5fc',
		serviceKey: 'sharepoint',
	},
	[ProductKeys3P.Github]: {
		containerId: '9bb160f8-1abb-4dc8-9780-ca5327e7ccc8_91300e36-3710-4f7b-aba2-95f43be1ac6f',
		serviceKey: 'github',
	},
	[ProductKeys3P.Figma]: {
		containerId: '652e701c-09ac-4e05-bd96-4c95b5c8cac5_ccaebdc1-68e5-406a-b1f4-efcc5962aa40',
		serviceKey: 'figma',
	},
	[ProductKeys3P.Teams]: {
		containerId: 'd27ee9f8-f60b-4076-910b-f16da23b7df4_b3763282-4b2e-4b4b-b924-3c8ba8f6e218',
		serviceKey: 'ms-teams',
	},
	[ProductKeys3P.Dropbox]: {
		containerId: 'd7a3169c-13c8-4b76-96b6-e8f9f849313d_d73e8370-9035-494d-ae16-20854375cb6f',
		serviceKey: 'dropbox',
	},
	[ProductKeys3P.Airtable]: {
		containerId: '070278f6-260c-420f-be4f-a7942a712c23_c2fe283b-51d2-4a0a-8e7f-4ce1199e0a10',
		serviceKey: 'airtable',
	},
	[ProductKeys3P.LaunchDarkly]: {
		containerId: 'd63e3423-0a85-461f-9650-7f26342c62bf_49e197a6-c428-442e-a5ca-f9d4889c82d8',
		serviceKey: 'launchdarkly',
	},
	[ProductKeys3P.ClickUp]: {
		containerId: 'fe20ccd3-b054-4139-aacb-8d98e086c16d_0b40b3c7-4c18-40e3-a030-101c94347723',
		serviceKey: 'clickup',
	},
	[ProductKeys3P.Pipedrive]: {
		containerId: '6012b82a-da48-44ff-af6e-da1c73b279f6_e6a2cefa-c9fe-4d50-b8ea-36391a2d41e6',
		serviceKey: 'pipedrive',
	},
	[ProductKeys3P.Gitlab]: {
		containerId: 'a7b83eeb-482d-402d-b87d-21ede67e73ed_3082dfec-249f-45d9-b84d-b49d256874e9',
		serviceKey: 'gitlab',
	},
	[ProductKeys3P.Docusign]: {
		containerId: 'ec9d65b4-72ee-4030-8dd1-e393026cf335_f4f50986-99a8-4f96-8b52-917d4c6bbbb7',
		serviceKey: 'docusign',
	},
	[ProductKeys3P.Dovetail]: {
		containerId: 'f9a3bf31-88d5-4b5a-80c2-cc32670259a2_c0c8e23b-ec93-45bb-928c-91b9671f0041',
		serviceKey: 'dovetail',
	},
	[ProductKeys3P.Box]: {
		containerId: '43cd20d8-11a7-4910-8789-06523fb2e6db_957783c9-4448-4d91-93dc-0ad7b24a327a',
		serviceKey: 'box',
	},
	[ProductKeys3P.AdobeXD]: {
		containerId: 'c1225a08-acd6-4908-84a7-d5b573f14cc1_e701a1e0-b819-4077-99ac-0b3525510c66',
		serviceKey: 'adobexd',
	},
	[ProductKeys3P.Asana]: {
		containerId: '35462eee-abe5-4873-ba7a-610d4c85f640_cb8525b3-26b3-467d-b42c-cb944e912311',
		serviceKey: 'asana',
	},
	[ProductKeys3P.Smartsheet]: {
		containerId: '48cb55a0-db03-4ce4-8f76-1f986f734652_e6b58d0f-fa9f-4543-9b67-5957b73dee6f',
		serviceKey: 'smartsheet',
	},
	[ProductKeys3P.Mural]: {
		containerId: '95d1bad0-96d7-40a8-996f-b8226dfd8fa1_d6095215-c4a5-4be1-bdd9-7065ed65ae37',
		serviceKey: 'mural',
	},
	[ProductKeys3P.PowerBI]: {
		containerId: 'ea3c5a46-228c-47ea-8c4b-f18e0f17fe8c_abc5ee35-a08c-4481-aa26-0439ce58b4c4',
		serviceKey: 'power-bi',
	},
	[ProductKeys3P.Miro]: {
		containerId: 'e37bcd89-df89-4e65-90f9-98a92fa2dbd0_7c1ec941-d9e7-40be-96fb-69fae6b6bfd2',
		serviceKey: 'miro',
	},
	[ProductKeys3P.Hubspot]: {
		containerId: 'aca58c62-2fb0-4abc-93f0-08bc704ab82a_c39eff84-66f8-4fa9-bb75-768ac83b5f6d',
		serviceKey: 'hubspot',
	},
	[ProductKeys3P.Notion]: {
		containerId: '60913f14-f7e5-465a-9df7-a5f7974caad6_80617f98-fa46-4b5e-9c8e-a54bc42b7c3e',
		serviceKey: 'notion',
	},
	[ProductKeys3P.Sentry]: {
		containerId: '59ec4558-3649-433e-a6b9-7964ae040b78_39660d3a-f2c9-446a-9a53-db433bbc7c2d',
		serviceKey: 'sentry',
	},
	[ProductKeys3P.PagerDuty]: {
		containerId: '2db77a65-0037-45ea-9f57-642051d86ff5_dd18e9a6-e52a-4b80-9a2f-1aa487c9f747',
		serviceKey: 'pagerduty',
	},
	[ProductKeys3P.Salesforce]: {
		containerId: 'd62184e4-87e4-41b2-9321-8b3c1dede9d7_433a0c54-84d5-41ee-9c21-684887b34330',
		serviceKey: 'salesforce',
	},
	[ProductKeys3P.Amplitude]: {
		containerId: '953d4aed-a528-4a5e-9f84-fe42741063f5_eed898af-fb54-4a76-ba73-8d6261639a1a',
		serviceKey: 'amplitude',
	},
	[ProductKeys3P.Lucid]: {
		containerId: 'd188e82f-311c-429d-a17f-bde97037890e_937a3756-c797-4f04-bde1-3eba582b91cf',
		serviceKey: 'lucid',
	},
	[ProductKeys3P.Zeplin]: {
		containerId: '2e14b294-385f-4d3c-a0a0-3e29e1f3a30b_f156da1b-5880-4fc1-ae67-2581ab8fda87',
		serviceKey: 'zeplin',
	},
	[ProductKeys3P.Todoist]: {
		containerId: '6ff1e497-21ac-4f8e-ac74-b869e2c3f274_0f5c1a58-fdae-41ae-841d-2bc5a2875c38',
		serviceKey: 'todoist',
	},
	[ProductKeys3P.Stripe]: {
		containerId: '48824814-f7d3-465a-9535-f589940c8e5a_8a330cd4-3af2-4c32-8c8e-8e751a3fad8a',
		serviceKey: 'stripe',
	},
	[ProductKeys3P.AzureDevops]: {
		// containerId sourced from the canonical Confluence page
		// (page id 6649924469). serviceKey assumed identical to prod —
		// ai-3p-connector's application-staging.yml does NOT declare a
		// per-provider serviceKey for Azure DevOps, but every other
		// provider in this file uses the same serviceKey across both
		// environments, so the prod value is the safe assumption.
		containerId: '784f3542-1a1d-4f49-8769-f3c2cb1e83b3_b91efd28-4d10-43d4-bcae-a5b0fef72bb1',
		serviceKey: 'azuredevops',
	},
	[ProductKeys3P.Webex]: {
		// containerId sourced from the canonical Confluence page
		// (page id 6649924469). serviceKey assumed identical to prod
		// (see note on AzureDevops above).
		containerId: 'ead13644-c3c5-4f8d-8d31-58be86dc7c8e_cedff546-1b81-4f3c-b65d-30c4cb2d8c61',
		serviceKey: 'webex',
	},
};

// ---------------------------------------------------------------------------
// Environment detection.
//
// We support TWO complementary strategies:
//
//  1. Hostname-based detection (`isProdHostname`) — used BEFORE we've talked
//     to the outbound-auth gateway, e.g. to build a Connect URL the moment
//     the user clicks. It's a reasonable best-guess but it gets `localhost`
//     wrong when the local dev server proxies to a real prod gateway
//     (which is the common Confluence local-dev setup).
//
//  2. Payload-based detection (`detectEnvFromPayload`) — once we have an
//     outbound-auth response, we can look at the container UUIDs the
//     gateway actually returned and decide which environment they belong
//     to. Container UUIDs are environment-scoped (prod ≠ staging) and
//     globally unique within an env, so a single matching UUID is a
//     deterministic signal.
//
// IMPORTANT: We never cross-reference between environments. A user
// authenticated against staging-Slack is NOT considered connected to
// prod-Slack — they are independent OAuth grants in independent gateways.
// `isAuthenticatedForProduct` therefore picks ONE env (the one the
// payload came from, if provided; otherwise hostname-based) and matches
// only against that env's UUIDs.
// ---------------------------------------------------------------------------

export type SmartLinkEnv = 'prod' | 'staging';

const ATLASSIAN_ID_BASE_URLS: Record<SmartLinkEnv, string> = {
	prod: 'https://id.atlassian.com',
	staging: 'https://id.stg.internal.atlassian.com',
};

const CONTAINER_MAPS: Record<
	SmartLinkEnv,
	Partial<Record<ProductKeys3PType, SmartLinkContainerMapping>>
> = {
	prod: SMARTLINK_CONTAINERS_PROD,
	staging: SMARTLINK_CONTAINERS_STAGING,
};

const isProdHostname = (): boolean => {
	if (typeof window === 'undefined' || !window.location) {
		return true;
	}
	return window.location.hostname.endsWith('.atlassian.net');
};

const getEnvFromHostname = (): SmartLinkEnv => (isProdHostname() ? 'prod' : 'staging');

/**
 * Determines which environment the outbound-auth gateway actually responded
 * from by tallying the container UUIDs in the payload against both maps.
 * The map with more matches wins. Returns `null` when no container UUIDs
 * are recognised in either map — in that case callers should fall back to
 * `getEnvFromHostname()`.
 *
 * Notes:
 *  - We only count UUIDs we recognise; the gateway will also return many
 *    unrelated containers (Bitbucket OAuth, Compass connectors, etc.) and
 *    those are silently ignored by the tally.
 *  - Ties are extremely unlikely (UUIDs are env-scoped) but if one does
 *    occur we prefer `prod` because that's the safer default in any
 *    user-facing context.
 */
export const detectEnvFromPayload = (
	payload: OutboundAuthAccountsPayload | null,
): SmartLinkEnv | null => {
	if (!payload || !Array.isArray(payload.containers) || payload.containers.length === 0) {
		return null;
	}
	const prodUuids = new Set(Object.values(SMARTLINK_CONTAINERS_PROD).map((m) => m?.containerId));
	const stagingUuids = new Set(
		Object.values(SMARTLINK_CONTAINERS_STAGING).map((m) => m?.containerId),
	);
	let prodHits = 0;
	let stagingHits = 0;
	for (const c of payload.containers) {
		const id = c.container?.containerId;
		if (!id) {
			continue;
		}
		if (prodUuids.has(id)) {
			prodHits++;
		}
		if (stagingUuids.has(id)) {
			stagingHits++;
		}
	}
	if (prodHits === 0 && stagingHits === 0) {
		return null;
	}
	return prodHits >= stagingHits ? 'prod' : 'staging';
};

const getAtlassianIdBaseUrl = (env: SmartLinkEnv): string => ATLASSIAN_ID_BASE_URLS[env];

const getContainerMap = (
	env: SmartLinkEnv,
): Partial<Record<ProductKeys3PType, SmartLinkContainerMapping>> => CONTAINER_MAPS[env];

// ---------------------------------------------------------------------------
// Public helpers.
// ---------------------------------------------------------------------------

/**
 * Fetches the user's full outbound-auth account list, using the canonical
 * `fetchAccountsByAaId` client from `@atlassian/search-client/outbound-auth`.
 *
 * The shared client gives us a 30-minute in-memory cache (keyed on aaId) and
 * built-in degradation handling, so the post-office banner and onboarding
 * modal can both mount the hook in the same session without duplicating the
 * network request.
 *
 * Returns `null` on any error so callers can safely fall back to whatever
 * they were doing before — the helper never throws.
 *
 * @param aaId The user's Atlassian Account ID.
 * @param options.forceRefresh When `true`, bypass the in-memory cache and
 *   pull a fresh payload from the gateway. Needed by callers that have just
 *   completed an OAuth handshake and need to observe the new account state
 *   (otherwise the cached pre-OAuth payload would be returned within the
 *   30-minute TTL, causing the optimistic UI flip to revert). Defaults to
 *   `false` so initial mounts keep benefitting from the cache.
 */
export const fetchOutboundAuthAccounts = async (
	aaId: string | undefined,
	{ forceRefresh = false }: { forceRefresh?: boolean } = {},
): Promise<OutboundAuthAccountsPayload | null> => {
	if (!aaId) {
		return null;
	}

	try {
		const response = await fetchAccountsByAaId({
			aaid: aaId,
			// When `forceRefresh` is true, bypass the cache (always hit the
			// gateway). Otherwise, prefer cached values over the network when
			// they are still warm — the post-office banner re-mounts on most
			// navigation events and we don't want to thrash the gateway.
			onlyRefreshIfExpired: !forceRefresh,
			returnExpired: true,
		});

		if (!response || !Array.isArray(response.containers)) {
			return null;
		}
		return response;
	} catch {
		return null;
	}
};

/**
 * Returns true iff the outbound-auth payload contains at least one
 * `available` account in the container whose UUID matches the canonical
 * container for the given product key.
 *
 * Returns false (not "unknown") when:
 *   - The payload is null/empty.
 *   - No mapping exists for the product key (we can't make a claim either
 *     way, so defaulting to "not connected" is the safe choice — the CTA
 *     will show but a click will fail gracefully because callers also
 *     filter providers without a mapping).
 *   - No account in the matching container has `available` status (expired
 *     / refreshNeeded are treated as "needs reconnect").
 *
 * The matching strategy mirrors the production implementation in
 * `platform/packages/jira-ai/repository-choice/src/useGithubOauth.tsx`:
 *   1. Find the container by UUID.
 *   2. Optionally narrow down by serviceKey (some shared containers host
 *      multiple services).
 *   3. Look for at least one `available` account.
 */
export const isAuthenticatedForProduct = (
	payload: OutboundAuthAccountsPayload | null,
	productKey: ProductKeys3PType | string,
	env?: SmartLinkEnv,
): boolean => {
	if (!payload || !Array.isArray(payload.containers) || payload.containers.length === 0) {
		return false;
	}

	// Prefer the env we detect from the payload itself (deterministic — the
	// gateway can only return UUIDs from one env at a time). Fall back to
	// hostname-based detection only if the payload contains no recognised
	// UUIDs in either map (defensive — should be unreachable since the
	// gateway always returns at least the user's atlassian-token-service-key
	// container, plus any Smart Link grants).
	const resolvedEnv = env ?? detectEnvFromPayload(payload) ?? getEnvFromHostname();
	const mapping = getContainerMap(resolvedEnv)[productKey as ProductKeys3PType];
	if (!mapping) {
		return false;
	}

	const matchingContainer = payload.containers.find(
		(c: OutboundAuthContainer) => c.container?.containerId === mapping.containerId,
	);
	if (!matchingContainer) {
		return false;
	}

	const services = Array.isArray(matchingContainer.services) ? matchingContainer.services : [];
	// Filter services by the expected serviceKey. For unique-container
	// providers this is a no-op (one service per container). For shared
	// containers it ensures we only look at accounts owned by the right
	// service.
	const matchingServices = services.filter((s) => s.service?.serviceKey === mapping.serviceKey);

	return matchingServices.some((s) =>
		(Array.isArray(s.accounts) ? s.accounts : []).some((account) => account.status === 'available'),
	);
};

/**
 * Builds the canonical OAuth start URL for a Smart Link provider using the
 * UUID-based container mapping. Returns `null` if no mapping exists for the
 * given product key — callers are expected to filter such providers out
 * rather than render a broken Connect button.
 *
 * This replaces the old `buildFallbackAuthUrl({ cloudId, serviceKey })`
 * helper, which incorrectly passed `cloudId` as `containerId`. Smart Link
 * connectors live in fixed UUID-based containers shared across all tenants,
 * not in per-tenant containers.
 */
export const buildSmartLinkAuthUrl = (
	productKey: ProductKeys3PType | string,
	env?: SmartLinkEnv,
): string | null => {
	const resolvedEnv = env ?? getEnvFromHostname();
	const mapping = getContainerMap(resolvedEnv)[productKey as ProductKeys3PType];
	if (!mapping) {
		return null;
	}

	const baseUrl = getAtlassianIdBaseUrl(resolvedEnv);
	const params = new URLSearchParams({
		serviceKey: mapping.serviceKey,
		containerId: mapping.containerId,
	});
	if (mapping.defaultScopes?.length) {
		params.set('scopes', mapping.defaultScopes.join(' '));
	}

	// URLSearchParams encodes spaces as '+'; gateway expects RFC 3986 '%20'.
	return `${baseUrl}/outboundAuth/start?${params.toString().replace(/\+/g, '%20')}`;
};

/**
 * Convenience: returns true iff a Smart Link container mapping exists for the
 * given product key in the supplied (or hostname-derived) environment.
 * Useful for filtering the fallback connector list.
 */
export const hasSmartLinkContainerMapping = (
	productKey: ProductKeys3PType | string,
	env?: SmartLinkEnv,
): boolean => {
	const resolvedEnv = env ?? getEnvFromHostname();
	return Boolean(getContainerMap(resolvedEnv)[productKey as ProductKeys3PType]);
};
